import { NetworkIntegrityAggregate, 
  NetworkIntegrityAggregateResults,
  NetworkIntegrityProviderPaginatedResult,
  NetworkIntegrityViewAllByGroupResult, 
  NetworkIntegrityProviderBreakdownResult, 
  NetworkIntegrityProviderTrend,
  NetworkIntegrityExcessWarningBreakdownResult,
  NetworkIntegrityAdequacyImpactResult,
  NetworkIntegrityAdequacyImpactProvider,
  NetworkIntegrityAdequacyImpactProviderGap,
  NetworkIntegrityProvider,
  NetworkIntegrityProviderSelectionStateDto,
  NetworkIntegrityFilterRequest,
  NetworkIntegrityJobStatusResponse,
} from "@app/projects/network-assessment/network-integrity/models";
import { EntityTypeEnum } from "@app/projects/enumerators";
import { NetworkIntegrityActionStatus } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import ServiceBase from "@app/shared/serviceBase";

export default class NetworkIntegrityService extends ServiceBase {

  private generateUrlPath = (endpoint: string, customProjectId: number, projectRunConfigurationId: string, providerType: string): string => {
    return `networkintegrity/projects/${customProjectId}/project-run-configurations/${projectRunConfigurationId}/provider-types/${providerType}/${endpoint}`;
  }

  public async getJobStatus(customProjectId: number, projectRunConfigurationId: string, providerType: string, isSecondary: boolean = false): Promise<NetworkIntegrityJobStatusResponse> {
    const path = this.generateUrlPath(`job-status?isSecondary=${isSecondary}`, customProjectId, projectRunConfigurationId, providerType);
    return await this.makeGet<NetworkIntegrityJobStatusResponse>(path);
  }

  public async resubmitFailedJobs(customProjectId: number, projectRunConfigurationId: string): Promise<void> {
    const path = `networkintegrity/projects/${customProjectId}/project-run-configurations/${projectRunConfigurationId}/resubmit-failed-jobs`;
    await this.makePost<void>(path, {});
  }

  public async getHasProcessedTerminations(customProjectId: number, projectRunConfigurationId: string, signal?: AbortSignal): Promise<boolean> {
    const path = `networkintegrity/projects/${customProjectId}/project-run-configurations/${projectRunConfigurationId}/has-processed-terminations`;
    return await this.makeGet<boolean>(path, undefined, undefined, false, undefined, signal);
  }

  public async getNetworkSummaryResult(customProjectId: number, projectRunConfigurationId: string, providerType: string, regionSetIds: number[] = [], specialtyCodes: string[] = []): Promise<NetworkIntegrityAggregate[]> {
    const path = this.generateUrlPath("network-summary-result", customProjectId, projectRunConfigurationId, providerType);
    const requestBody: NetworkIntegrityFilterRequest = { regionSetIds, specialtyCodes };
    return await this.makePost<NetworkIntegrityAggregate[]>(path, requestBody);
  }

  public async getAdequacyImpactResult(customProjectId: number, projectRunConfigurationId: string, providerType: string, regionSetIds: number[] = [], specialtyCodes: string[] = []): Promise<NetworkIntegrityAdequacyImpactResult> {
    const path = this.generateUrlPath("adequacy-impact-result", customProjectId, projectRunConfigurationId, providerType);
    const requestBody: NetworkIntegrityFilterRequest = { regionSetIds, specialtyCodes };
    return await this.makePost<NetworkIntegrityAdequacyImpactResult>(path, requestBody);
  }

  public async getAdequacyImpactProviderGaps(customProjectId: number, projectRunConfigurationId: number, providerType: string, impactType: string, regionSetIds: number[] = [], specialtyCodes: string[] = [], signal?: AbortSignal): Promise<NetworkIntegrityAdequacyImpactProviderGap[]> {
    const path = this.generateUrlPath(`adequacy-impact-provider-gaps/${impactType}`, customProjectId, projectRunConfigurationId.toString(), providerType);
    const requestBody: NetworkIntegrityFilterRequest = { regionSetIds, specialtyCodes };
    return await this.makePost<NetworkIntegrityAdequacyImpactProviderGap[]>(path, requestBody, undefined, false, "application/json", true, undefined, signal);
  }

  public async getAdequacyImpactProviderGapDetails(customProjectId: number, projectRunConfigurationId: number, providerType: string, body: string[]): Promise<NetworkIntegrityAdequacyImpactProvider[]> {
    const path = this.generateUrlPath(`adequacy-impact-provider-gap-details`, customProjectId, projectRunConfigurationId.toString(), providerType);
    return await this.makePost<NetworkIntegrityAdequacyImpactProvider[]>(path, body);
  }

  public async getOptionalExclusionResults(customProjectId: number, projectRunConfigurationId: string, providerType: string, regionSetIds: number[] = [], specialtyCodes: string[] = []): Promise<NetworkIntegrityAggregate[]> {
    const path = this.generateUrlPath("optional-exclusion-results", customProjectId, projectRunConfigurationId, providerType);
    const requestBody: NetworkIntegrityFilterRequest = { regionSetIds, specialtyCodes };
    return await this.makePost<NetworkIntegrityAggregate[]>(path, requestBody);
  }

  public async getCriticalAlertResults(customProjectId: number, projectRunConfigurationId: string, providerType: string, regionSetIds: number[] = [], specialtyCodes: string[] = []): Promise<NetworkIntegrityAggregate[]> {
    const path = this.generateUrlPath("critical-alert-results", customProjectId, projectRunConfigurationId, providerType);
    const requestBody: NetworkIntegrityFilterRequest = { regionSetIds, specialtyCodes };
    return await this.makePost<NetworkIntegrityAggregate[]>(path, requestBody);
  }

  public async getAttestationBasedAlertResults(customProjectId: number, projectRunConfigurationId: string, providerType: string, regionSetIds: number[] = [], specialtyCodes: string[] = []): Promise<NetworkIntegrityAggregate[]> {
    const path = this.generateUrlPath("attestation-based-alert-results", customProjectId, projectRunConfigurationId, providerType);
    const requestBody: NetworkIntegrityFilterRequest = { regionSetIds, specialtyCodes };
    return await this.makePost<NetworkIntegrityAggregate[]>(path, requestBody);
  }

  public async getExcessWarningsResults(customProjectId: number, projectRunConfigurationId: string, providerType: string, regionSetIds: number[] = [], specialtyCodes: string[] = []): Promise<NetworkIntegrityAggregateResults> {
    const path = this.generateUrlPath("excess-warning-results", customProjectId, projectRunConfigurationId, providerType);
    const requestBody: NetworkIntegrityFilterRequest = { regionSetIds, specialtyCodes };
    return await this.makePost<NetworkIntegrityAggregateResults>(path, requestBody);
  }

  public async getExcessWarningBreakdownResults(customProjectId: number, projectRunConfigurationId: string, providerType: string, aggregateGroup: string, regionSetIds: number[] = [], specialtyCodes: string[] = [], npiEntityType: EntityTypeEnum | null = null): Promise<NetworkIntegrityExcessWarningBreakdownResult[]> {
    const path = this.generateUrlPath(`excess-warning-breakdown-results/${encodeURIComponent(aggregateGroup)}`, customProjectId, projectRunConfigurationId, providerType);
    const requestBody: NetworkIntegrityFilterRequest = { npiEntityType, regionSetIds, specialtyCodes };
    return await this.makePost<NetworkIntegrityExcessWarningBreakdownResult[]>(path, requestBody);
  }

  public async getProvidersByStateResults(customProjectId: number, projectRunConfigurationId: string, providerType: string, regionSetIds: number[] = [], specialtyCodes: string[] = []): Promise<NetworkIntegrityAggregateResults> {
    const path = this.generateUrlPath("providers-by-state-results", customProjectId, projectRunConfigurationId, providerType);
    const requestBody: NetworkIntegrityFilterRequest = { regionSetIds, specialtyCodes };
    return await this.makePost<NetworkIntegrityAggregateResults>(path, requestBody);
  }

  public async getProvidersByCountyResults(customProjectId: number, projectRunConfigurationId: string, providerType: string, regionSetIds: number[] = [], specialtyCodes: string[] = []): Promise<NetworkIntegrityAggregateResults> {
    const path = this.generateUrlPath("providers-by-county-results", customProjectId, projectRunConfigurationId, providerType);
    const requestBody: NetworkIntegrityFilterRequest = { regionSetIds, specialtyCodes };
    return await this.makePost<NetworkIntegrityAggregateResults>(path, requestBody);
  }

  public async getProvidersByZipCodeResults(customProjectId: number, projectRunConfigurationId: string, providerType: string, regionSetIds: number[] = [], specialtyCodes: string[] = []): Promise<NetworkIntegrityAggregateResults> {
    const path = this.generateUrlPath("providers-by-zip-code-results", customProjectId, projectRunConfigurationId, providerType);
    const requestBody: NetworkIntegrityFilterRequest = { regionSetIds, specialtyCodes };
    return await this.makePost<NetworkIntegrityAggregateResults>(path, requestBody);
  }

  public async getProvidersBySpecialtyResults(customProjectId: number, projectRunConfigurationId: string, providerType: string, regionSetIds: number[] = [], specialtyCodes: string[] = []): Promise<NetworkIntegrityAggregateResults> {
    const path = this.generateUrlPath("providers-by-specialty-results", customProjectId, projectRunConfigurationId, providerType);
    const requestBody: NetworkIntegrityFilterRequest = { regionSetIds, specialtyCodes };
    return await this.makePost<NetworkIntegrityAggregateResults>(path, requestBody);
  }

  public async getProvidersByGeocodeResults(customProjectId: number, projectRunConfigurationId: string, providerType: string, regionSetIds: number[] = [], specialtyCodes: string[] = []): Promise<NetworkIntegrityAggregateResults> {
    const path = this.generateUrlPath("providers-by-geocode-results", customProjectId, projectRunConfigurationId, providerType);
    const requestBody: NetworkIntegrityFilterRequest = { regionSetIds, specialtyCodes };
    return await this.makePost<NetworkIntegrityAggregateResults>(path, requestBody);
  }

  public async getProvidersByZipCodeClassResults(customProjectId: number, projectRunConfigurationId: string, providerType: string, regionSetIds: number[] = [], specialtyCodes: string[] = []): Promise<NetworkIntegrityAggregateResults> {
    const path = this.generateUrlPath("providers-by-zip-code-class-results", customProjectId, projectRunConfigurationId, providerType);
    const requestBody: NetworkIntegrityFilterRequest = { regionSetIds, specialtyCodes };
    return await this.makePost<NetworkIntegrityAggregateResults>(path, requestBody);
  }

  public async getProviderBreakdownTrendResults(customProjectId: number, projectRunConfigurationId: string, providerType: string, projectRunConfigurationDualType: string, regionSetIds: number[] = [], specialtyCodes: string[] = []): Promise<NetworkIntegrityProviderTrend[]> {
    const path = this.generateUrlPath(`network-integrity-trend/${projectRunConfigurationDualType}`, customProjectId, projectRunConfigurationId, providerType);
    const requestBody: NetworkIntegrityFilterRequest = { regionSetIds, specialtyCodes };
    return await this.makePost<NetworkIntegrityProviderTrend[]>(path, requestBody);
  }

  public async getProviders(
    customProjectId: number,
    projectRunConfigurationId: string,
    providerType: string,
    aggregateGroup: string,
    aggregateType: string,
    page: number = 1,
    pageSize: number = 100,
    search?: string,
    sortColumn: string = "npi",
    sortDirection: string = "asc",
    regionSetIds: number[] = [],
    specialtyCodes: string[] = [],
    actionStatus: NetworkIntegrityActionStatus = NetworkIntegrityActionStatus.All,
    npiEntityType: EntityTypeEnum | null = null
  ): Promise<NetworkIntegrityProviderPaginatedResult> {
    const path = this.generateUrlPath(`network-integrity-providers/${encodeURIComponent(aggregateGroup)}/${encodeURIComponent(aggregateType)}`, customProjectId, projectRunConfigurationId, providerType);
    return await this.makePost<NetworkIntegrityProviderPaginatedResult>(path, {
      actionStatus,
      npiEntityType,
      page,
      pageSize,
      regionSetIds,
      search: search || "",
      sortColumn,
      sortDirection,
      specialtyCodes
    });
  }

  public async getSelectedProviders(
    customProjectId: number,
    projectRunConfigurationId: string,
    providerType: string,
    aggregateGroup: string,
    aggregateType: string,
    body: NetworkIntegrityProviderSelectionStateDto
  ): Promise<NetworkIntegrityProvider[]> {
    const path = this.generateUrlPath(`network-integrity-providers/${encodeURIComponent(aggregateGroup)}/${encodeURIComponent(aggregateType)}/get-selected-providers`, customProjectId, projectRunConfigurationId, providerType);
    return await this.makePost<NetworkIntegrityProvider[]>(path, body);
  }

  public async getProviderIds(
    customProjectId: number,
    projectRunConfigurationId: string,
    providerType: string,
    aggregateGroup: string,
    aggregateType: string,
    body: NetworkIntegrityProviderSelectionStateDto
  ): Promise<string[]> {
    const path = this.generateUrlPath(`network-integrity-providers/${encodeURIComponent(aggregateGroup)}/${encodeURIComponent(aggregateType)}/ids`, customProjectId, projectRunConfigurationId, providerType);
    return await this.makePost<string[]>(path, body);
  }

  public async getProvidersBreakdown(customProjectId: number, projectRunConfigurationId: string, providerType: string, regionSetIds: number[] = [], specialtyCodes: string[] = []): Promise<NetworkIntegrityProviderBreakdownResult> {
    const path = this.generateUrlPath("providers-breakdown", customProjectId, projectRunConfigurationId, providerType);
    const requestBody: NetworkIntegrityFilterRequest = { regionSetIds, specialtyCodes };
    return await this.makePost<NetworkIntegrityProviderBreakdownResult>(path, requestBody);
  }

  public async getViewAllByAggregateGroupResults(customProjectId: number, projectRunConfigurationId: number, aggregateGroup: string, providerType: string, regionSetIds: number[] = [], specialtyCodes: string[] = []): Promise<NetworkIntegrityViewAllByGroupResult> {
    const path = this.generateUrlPath(`view-all/${encodeURIComponent(aggregateGroup)}`, customProjectId, projectRunConfigurationId.toString(), providerType);
    return await this.makePost<NetworkIntegrityViewAllByGroupResult>(path, { regionSetIds, specialtyCodes });
  }

  public async getAggregateCount(customProjectId: number, projectRunConfigurationId: string, providerType: string, aggregateGroup: string, aggregateType: string, regionSetIds: number[] = [], specialtyCodes: string[] = [], actionStatus: NetworkIntegrityActionStatus = NetworkIntegrityActionStatus.All, npiEntityType: EntityTypeEnum | null = null): Promise<number> {
    const path = this.generateUrlPath(`aggregate-count/${encodeURIComponent(aggregateGroup)}/${encodeURIComponent(aggregateType)}`, customProjectId, projectRunConfigurationId, providerType);
    return await this.makePost<number>(path, { actionStatus, npiEntityType, regionSetIds, specialtyCodes });
  }
}